import { X, Trash2, AlertTriangle } from "lucide-react";
import type { CleanupModalState } from "../modal/LinkedInJobsContext";

interface Props {
  modal: Exclude<CleanupModalState, null>;
  executing: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export default function CleanupConfirmModal({ modal, executing, error, onConfirm, onClose }: Props) {
  const title = modal.type === "archived"
    ? "Clean Up Archived Alerts"
    : `Delete ${modal.months}-Month+ Old Jobs`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">{title}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={executing}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview stats */}
        <div className="bg-surface-muted/60 border border-border/60 rounded-xl p-4 mb-5 space-y-2">
          <p className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wide mb-3">
            This will permanently delete:
          </p>
          {modal.type === "archived" ? (
            <>
              <StatRow label="Archived alerts" value={modal.preview.alert_count} />
              <StatRow label="Jobs fully removed" value={modal.preview.jobs_to_fully_delete} />
              {modal.preview.jobs_to_keep > 0 && (
                <StatRow
                  label="Jobs kept (linked to other TMs)"
                  value={modal.preview.jobs_to_keep}
                  muted
                />
              )}
            </>
          ) : (
            <>
              <StatRow label={`Jobs older than ${modal.months} months`} value={modal.preview.job_count} />
              <StatRow label="Linked alerts" value={modal.preview.alert_count} />
              <StatRow label="History entries" value={modal.preview.history_count} />
            </>
          )}
        </div>

        <p className="text-xs text-muted-foreground font-inter mb-5">
          This action cannot be undone.
        </p>

        {error && (
          <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={executing}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors disabled:opacity-40 font-inter"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={
              executing ||
              (modal.type === "archived" && modal.preview.alert_count === 0) ||
              (modal.type === "old_jobs" && modal.preview.job_count === 0)
            }
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter"
          >
            <Trash2 className="w-4 h-4" />
            {executing ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, muted = false }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm font-inter ${muted ? "text-muted-foreground" : "text-foreground"}`}>{label}</span>
      <span className={`text-sm font-semibold font-inter tabular-nums ${muted ? "text-muted-foreground" : value === 0 ? "text-muted-foreground" : "text-foreground"}`}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}
